import axios from 'axios';
import api from './api';

// Sync › project › Toolkit — the web port of the HCONNECT Migration Toolkit.
//
// Served by its OWN API gateway (code-toolkit), routed through the shared CloudFront front door at
// /toolkit*, so it goes through the same `api` client as everything else and inherits the Authorization
// header. Raw OHIP secrets never leave the backend.
//
// THE TOOLKIT WORKS AS A SYNC PROJECT. Every call carries `project` = { syncId, side? } (side 'source' only for an
// Opera Cloud → Opera Cloud project; target otherwise). The server reads that project's own OPERA Cloud connector —
// nothing about the connection is chosen or sent from the browser.
// THE LAMBDA IS ONE INSTANCE (reserved concurrency 1, for the OHIP limit) and a server job uses it too (toolkit
// jobs.js), so a screen's request can arrive while it is busy with a job's row. Lambda then refuses the call and API
// Gateway answers itself — a 5xx with its own { message } body, never the toolkit's { error: { message } }. Only that
// case is retried: the lambda never ran, so nothing was done twice. A toolkit error (it ran, and said no) is not.
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const notReached = (err) => {
    const r = err?.response;
    return !!r && [429, 500, 502, 503].includes(r.status) && !(r.data && r.data.error) && typeof r.data?.message === 'string';
};
const post = async (url, body) => {
    for (let attempt = 0; ; attempt++) {
        try {
            return await api.post(url, body);
        } catch (err) {
            if (attempt >= 6 || !notReached(err)) throw err;
            await sleep(Math.min(1000 * 2 ** attempt, 15000));
        }
    }
};

const toolkitService = {
    health: () => api.get('/toolkit/health'),

    testConnection: (project) => post('/toolkit/ohip/test', { ...project }),

    // Parse an uploaded v5 report: kind = 'ar-accounts' | 'ar-account-types' | … (GET /toolkit/health → fileKinds).
    parseFile: (key, kind, options = {}) => post('/toolkit/files/parse', { key, kind, options }),

    // Compare two uploaded reports, v5 and Opera Cloud (lambda compare.js): kind = 'reservations' | 'history-forecast'
    // | 'ar-ledger'. Returns { summary, rows (everything not a clean match), truncated }.
    compare: (project, kind, v5Key, cloudKey, options = {}) => post('/toolkit/compare', { ...project, kind, v5Key, cloudKey, options }),

    // Read-only OHIP lookups (lambda lookups.js): cashiers, hotel-currency, ar-transaction-code, …
    lookup: (project, kind, params = {}) => post('/toolkit/ohip/lookup', { ...project, kind, params }),

    // Accounts Receivable — one op per call (lambda ar.js). Writes are audited server-side.
    ar: (project, op, args = {}) => post('/toolkit/ar', { ...project, op, args }),

    // Deposit Ledger, Deposit Folio, Guest Ledger — one op per call (lambda ledgers.js). Writes are audited server-side.
    ledger: (project, op, args = {}) => post('/toolkit/ledger', { ...project, op, args }),

    // Property and reservation operations — check-in, OOO/OOS, HK status, rate refresh, same-day departures, market
    // codes, rooms (lambda property.js). list-* ops are reads; the rest are audited server-side.
    property: (project, op, args = {}) => post('/toolkit/property', { ...project, op, args }),

    // Internal Notes — v5 profile notes, reservation comments and block notes onto their Cloud records (lambda
    // notes.js). One record per call; audited server-side.
    notes: (project, op, args = {}) => post('/toolkit/notes', { ...project, op, args }),

    // UDFs — v5 profile / reservation UDF values onto their Cloud records (lambda udfs.js). Audited server-side.
    udfs: (project, op, args = {}) => post('/toolkit/udfs', { ...project, op, args }),

    // Block Grid Update (copies between the project's two Opera Clouds) and Event Management (lambda groups.js).
    groups: (project, op, args = {}) => post('/toolkit/groups', { ...project, op, args }),

    // On-premise reads (lambda onprem.js): a fixed catalogue query sent to the project's SyncConnect, then collected.
    // op = catalog | submit { key } | list { key? } | get { id }.
    onprem: (project, op, args = {}) => post('/toolkit/onprem', { ...project, op, args }),

    // Server jobs (lambda jobs.js): a screen's batch run by the toolkit-jobs Step Function instead of the browser.
    // op = start { screen, title, screenPath, itemsKey, fingerprint, options } | list { screen?, active? }
    //    | get { jobId, since } | items { jobId, after } | stop { jobId } | resume { jobId }.
    jobs: (project, op, args = {}) => post('/toolkit/jobs', { ...project, op, args }),

    // Working state on OpenSearch (storage.js), scoped to the project's property.
    storage: (project, op, args = {}) => post('/toolkit/storage', { ...project, op, args }),

    blocks: {
        parse: (key) => post('/toolkit/blocks/parse', { key }),
        cloud: (project, params) => post('/toolkit/blocks/cloud', { ...project, ...params }),
        compare: (params) => post('/toolkit/blocks/compare', params),
    },

    /**
     * Upload a working file and return its S3 key.
     *
     * Two steps on purpose. The API returns a presigned PUT and the file then goes STRAIGHT to S3 —
     * API Gateway caps a body at 10MB and base64 inflates binary by a third, and a v5 Block Information
     * report for a busy property is comfortably larger than that.
     *
     * The PUT uses a bare axios, not `api`: the presigned URL carries its own signature, and the
     * Authorization header our client attaches would invalidate it.
     */
    upload: async (file, onProgress) => {
        const { data } = await post('/toolkit/uploads/presign', {
            filename: file.name,
            contentType: file.type || 'application/octet-stream',
        });
        await axios.put(data.url, file, {
            headers: {
                'Content-Type': file.type || 'application/octet-stream',
                // Must match the signature exactly — the bucket is SSE-KMS and the presign declared it.
                'x-amz-server-side-encryption': 'aws:kms',
            },
            onUploadProgress: (e) => {
                if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
            },
        });
        return data.key;
    },
};

export default toolkitService;
